import React, { useState, useEffect } from 'react';
import { useBranding } from '../contexts/BrandingContext';
import { API_URL } from '../config/api';

const AdminBulkBooking = () => {
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor || '#667eea';
  const [courts, setCourts] = useState([]);
  const [sports, setSports] = useState([]);

  const [selectedSport, setSelectedSport] = useState('');
  const [selectedCourts, setSelectedCourts] = useState([]);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [bookingNotes, setBookingNotes] = useState('');
  const [ignoreUnavailable, setIgnoreUnavailable] = useState(true);
  const [bookingType, setBookingType] = useState('customer'); // 'customer' or 'block'

  // Customer details (same as Quick Booking)
  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: '',
    billNumber: '',
    paymentMode: 'cash'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');


  const daysOfWeek = [
    { value: 'all', label: 'All Days' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 0, label: 'Sun' }
  ];

  const generateAllTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startHour = hour.toString().padStart(2, '0');
        const startMinute = minute.toString().padStart(2, '0');
        const endHour = minute === 30 ? (hour + 1).toString().padStart(2, '0') : hour.toString().padStart(2, '0');
        const endMinute = minute === 30 ? '00' : '30';
        
        const startTime = `${startHour}:${startMinute}:00`;
        const endTime = `${endHour}:${endMinute}:00`;
        const displayTime = `${startHour}:${startMinute} - ${endHour}:${endMinute}`;
        
        slots.push({ 
          startTime, 
          endTime, 
          displayTime, 
          id: `${startTime}-${endTime}`,
          start_time: startTime,
          end_time: endTime
        });
      }
    }
    return slots;
  };

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/courts`);
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const data = await response.json();
      let courtsData = [];
      
      if (data.success && data.courts) {
        courtsData = data.courts;
      } else if (data.data) {
        courtsData = data.data;
      } else if (Array.isArray(data)) {
        courtsData = data;
      }
      
      setCourts(courtsData);
      
      const uniqueSports = [...new Set(courtsData.map(c => c.sport_type || c.sport_name))].filter(Boolean);
      setSports(uniqueSports);
      
      setError('');
    } catch (error) {
      console.error('Error fetching courts:', error);
      setError('Failed to connect to server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourts();
  }, []);

  const handleSportChange = (sport) => {
    setSelectedSport(sport);
    setSelectedCourts([]);
  };

  const handleDayToggle = (dayValue) => {
    if (dayValue === 'all') {
      if (selectedDays.length === 7) {
        setSelectedDays([]);
      } else {
        setSelectedDays([0, 1, 2, 3, 4, 5, 6]);
      }
    } else {
      setSelectedDays(prev => {
        if (prev.includes(dayValue)) {
          return prev.filter(d => d !== dayValue);
        } else {
          return [...prev, dayValue];
        }
      });
    }
  };

  const handleCourtToggle = (courtId) => {
    setSelectedCourts(prev => {
      if (prev.includes(courtId)) {
        return prev.filter(id => id !== courtId);
      } else {
        return [...prev, courtId];
      }
    });
  };

  const handleTimeSlotToggle = (slot) => {
    setSelectedTimeSlots(prev => {
      const exists = prev.find(s => s.id === slot.id);
      if (exists) {
        return prev.filter(s => s.id !== slot.id);
      } else {
        return [...prev, slot].sort((a, b) => a.startTime.localeCompare(b.startTime));
      }
    });
  };

  const selectAllCourts = () => {
    const filteredCourts = getFilteredCourts();
    setSelectedCourts(filteredCourts.map(c => c.id));
  };

  const deselectAllCourts = () => {
    setSelectedCourts([]);
  };

  const getFilteredCourts = () => {
    if (!selectedSport) return courts;
    return courts.filter(c => (c.sport_type || c.sport_name) === selectedSport);
  };

  const getDatesInRange = (startDate, endDate, daysFilter) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay();
      if (daysFilter.includes(dayOfWeek)) {
        dates.push(d.toISOString().split('T')[0]);
      }
    }
    
    return dates;
  };

  const handleBulkBooking = async (e) => {
    e.preventDefault();

    if (!selectedSport) {
      setError('Please select a sport');
      return;
    }

    if (selectedCourts.length === 0) {
      setError('Please select at least one court');
      return;
    }

    if (!dateRange.from || !dateRange.to) {
      setError('Please select date range');
      return;
    }

    if (selectedDays.length === 0) {
      setError('Please select at least one day of the week');
      return;
    }

    if (selectedTimeSlots.length === 0) {
      setError('Please select at least one time slot');
      return;
    }

    // Customer booking validation
    if (bookingType === 'customer') {
      if (!customerData.name.trim()) {
        setError('Please enter customer name');
        return;
      }
      if (!customerData.phone.trim()) {
        setError('Please enter phone number');
        return;
      }
      if (!customerData.billNumber.trim()) {
        setError('Please enter bill number');
        return;
      }
    } else {
      // Block validation
      if (!bookingNotes || bookingNotes.trim().length < 10) {
        setError('Please provide a reason for blocking (minimum 10 characters)');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      const dates = getDatesInRange(dateRange.from, dateRange.to, selectedDays);

      let successCount = 0;
      let failCount = 0;
      let bookingIndex = 0;

      // Calculate total bookings for bill number suffix
      const totalBookings = selectedCourts.length * dates.length * selectedTimeSlots.length;

      for (const courtId of selectedCourts) {
        for (const date of dates) {
          for (const slot of selectedTimeSlots) {
            bookingIndex++;
            try {
              const isCustomerBooking = bookingType === 'customer';

              // Generate bill number with suffix for bulk bookings
              const billNumber = isCustomerBooking && totalBookings > 1
                ? `${customerData.billNumber}-${bookingIndex}`
                : customerData.billNumber;

              const response = await fetch(`${API_URL}/bookings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  court_id: courtId,
                  booking_date: date,
                  start_time: slot.start_time,
                  end_time: slot.end_time,
                  customer_name: isCustomerBooking ? customerData.name : 'BLOCKED',
                  customer_phone: isCustomerBooking ? customerData.phone : '0000000000',
                  customer_email: isCustomerBooking ? (customerData.email || null) : 'admin-block@system.local',
                  bill_number: isCustomerBooking ? billNumber : null,
                  payment_mode: isCustomerBooking ? customerData.paymentMode : 'cash',
                  notes: isCustomerBooking ? (bookingNotes || null) : `ADMIN BLOCK: ${bookingNotes}`,
                  source: 'frontdesk'
                })
              });

              const data = await response.json();

              if (data.success || data.id) {
                successCount++;
              } else {
                if (!ignoreUnavailable) {
                  failCount++;
                }
              }
            } catch (err) {
              console.error('Error creating booking:', err);
              if (!ignoreUnavailable) {
                failCount++;
              }
            }
          }
        }
      }

      if (successCount > 0) {
        const actionWord = bookingType === 'customer' ? 'booked' : 'blocked';
        setSuccess(`Successfully ${actionWord} ${successCount} slots across ${dates.length} days for ${selectedCourts.length} court(s)${failCount > 0 ? `. ${failCount} slots failed (already booked).` : ''}`);

        // Reset form
        setSelectedSport('');
        setSelectedCourts([]);
        setDateRange({ from: '', to: '' });
        setSelectedDays([]);
        setSelectedTimeSlots([]);
        setBookingNotes('');
        setCustomerData({ name: '', phone: '', email: '', billNumber: '', paymentMode: 'cash' });
      } else {
        setError('No slots were created. All slots may already be booked.');
      }
    } catch (error) {
      console.error('Error bulk booking:', error);
      setError('Failed to create bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedSport('');
    setSelectedCourts([]);
    setDateRange({ from: '', to: '' });
    setSelectedDays([]);
    setSelectedTimeSlots([]);
    setBookingNotes('');
    setBookingType('customer');
    setCustomerData({ name: '', phone: '', email: '', billNumber: '', paymentMode: 'cash' });
    setError('');
    setSuccess('');
  };

  const getMinDate = () => new Date().toISOString().split('T')[0];
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  const filteredCourts = getFilteredCourts();

  return (
    <div className="admin-page">
      {success && <div className="admin-alert success">{success}</div>}
      {error && <div className="admin-alert error">{error}</div>}

      <div className="admin-card">
        <div className="admin-card-header">Bulk Booking</div>
        <div className="admin-card-body">
          <form onSubmit={handleBulkBooking}>
            {/* Booking Type Toggle */}
            <div style={{ marginBottom: '20px' }}>
              <label className="admin-label">Booking Type</label>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: bookingType === 'customer' ? `${primaryColor}15` : '#f9fafb', border: `2px solid ${bookingType === 'customer' ? primaryColor : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: bookingType === 'customer' ? '600' : '400' }}>
                  <input type="radio" name="bookingType" value="customer" checked={bookingType === 'customer'} onChange={(e) => setBookingType(e.target.value)} style={{ accentColor: primaryColor }} />
                  Customer Booking
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: bookingType === 'block' ? `${primaryColor}15` : '#f9fafb', border: `2px solid ${bookingType === 'block' ? primaryColor : '#e5e7eb'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: bookingType === 'block' ? '600' : '400' }}>
                  <input type="radio" name="bookingType" value="block" checked={bookingType === 'block'} onChange={(e) => setBookingType(e.target.value)} style={{ accentColor: primaryColor }} />
                  Block Slots
                </label>
              </div>
            </div>
            {/* Sport & Courts Row */}
            <div className="admin-form-row" style={{ marginBottom: '16px' }}>
              <div className="admin-field">
                <label className="admin-label">Sport *</label>
                <select value={selectedSport} onChange={(e) => handleSportChange(e.target.value)} required className="admin-select">
                  <option value="">Select a sport</option>
                  {sports.map(sport => (<option key={sport} value={sport}>{sport}</option>))}
                </select>
              </div>
              <div className="admin-field">
                <label className="admin-label">Start Date *</label>
                <input type="date" value={dateRange.from} onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))} min={getMinDate()} max={getMaxDate()} required className="admin-input" />
              </div>
              <div className="admin-field">
                <label className="admin-label">End Date *</label>
                <input type="date" value={dateRange.to} onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))} min={dateRange.from || getMinDate()} max={getMaxDate()} required className="admin-input" />
              </div>
            </div>

            {/* Courts Selection */}
            {selectedSport && (
              <div style={{ marginBottom: '16px' }}>
                <label className="admin-label">Courts</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#f3f4f6', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                    <input type="checkbox" checked={selectedCourts.length === filteredCourts.length && filteredCourts.length > 0} onChange={() => selectedCourts.length === filteredCourts.length ? deselectAllCourts() : selectAllCourts()} />
                    <strong>All</strong>
                  </label>
                  {filteredCourts.map(court => (
                    <label key={court.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: selectedCourts.includes(court.id) ? `${primaryColor}15` : '#f9fafb', border: `1px solid ${selectedCourts.includes(court.id) ? primaryColor : '#e5e7eb'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                      <input type="checkbox" checked={selectedCourts.includes(court.id)} onChange={() => handleCourtToggle(court.id)} />
                      {court.court_name || court.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Days Selection */}
            <div style={{ marginBottom: '16px' }}>
              <label className="admin-label">Days of Week</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {daysOfWeek.map(day => (
                  <label key={day.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: (day.value === 'all' ? selectedDays.length === 7 : selectedDays.includes(day.value)) ? `${primaryColor}15` : '#f9fafb', border: `1px solid ${(day.value === 'all' ? selectedDays.length === 7 : selectedDays.includes(day.value)) ? primaryColor : '#e5e7eb'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                    <input type="checkbox" checked={day.value === 'all' ? selectedDays.length === 7 : selectedDays.includes(day.value)} onChange={() => handleDayToggle(day.value)} />
                    {day.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Time Slots */}
            <div style={{ marginBottom: '16px' }}>
              <label className="admin-label">Time Slots</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '6px', maxHeight: '200px', overflowY: 'auto', padding: '8px', background: '#f9fafb', borderRadius: '6px', marginTop: '8px' }}>
                {generateAllTimeSlots().map(slot => {
                  const isSelected = selectedTimeSlots.find(s => s.id === slot.id);
                  return (
                    <button key={slot.id} type="button" onClick={() => handleTimeSlotToggle(slot)} style={{ padding: '6px 8px', border: `1px solid ${isSelected ? primaryColor : '#e5e7eb'}`, borderRadius: '4px', background: isSelected ? primaryColor : 'white', color: isSelected ? 'white' : '#374151', fontSize: '12px', cursor: 'pointer' }}>
                      {slot.displayTime}
                    </button>
                  );
                })}
              </div>
              {selectedTimeSlots.length > 0 && (
                <div style={{ marginTop: '8px', padding: '8px 12px', background: '#ecfdf5', borderRadius: '6px', fontSize: '13px', color: '#065f46' }}>
                  {selectedTimeSlots.length} slots selected
                </div>
              )}
            </div>

            {/* Customer Details (shown for customer booking) */}
            {bookingType === 'customer' && (
              <div style={{ marginBottom: '16px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <label className="admin-label" style={{ marginBottom: '12px', display: 'block' }}>Customer Details</label>
                <div className="admin-form-row" style={{ marginBottom: '12px' }}>
                  <div className="admin-field">
                    <label className="admin-label" style={{ fontSize: '12px' }}>Customer Name *</label>
                    <input
                      type="text"
                      value={customerData.name}
                      onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                      placeholder="Enter name"
                      className="admin-input"
                      required={bookingType === 'customer'}
                    />
                  </div>
                  <div className="admin-field">
                    <label className="admin-label" style={{ fontSize: '12px' }}>Phone Number *</label>
                    <input
                      type="tel"
                      value={customerData.phone}
                      onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                      placeholder="Enter phone"
                      className="admin-input"
                      required={bookingType === 'customer'}
                    />
                  </div>
                </div>
                <div className="admin-form-row" style={{ marginBottom: '12px' }}>
                  <div className="admin-field">
                    <label className="admin-label" style={{ fontSize: '12px' }}>Bill Number *</label>
                    <input
                      type="text"
                      value={customerData.billNumber}
                      onChange={(e) => setCustomerData({ ...customerData, billNumber: e.target.value.toUpperCase() })}
                      placeholder="Enter bill/receipt number"
                      className="admin-input"
                      style={{ textTransform: 'uppercase' }}
                      required={bookingType === 'customer'}
                    />
                  </div>
                  <div className="admin-field">
                    <label className="admin-label" style={{ fontSize: '12px' }}>Payment Mode *</label>
                    <select
                      value={customerData.paymentMode}
                      onChange={(e) => setCustomerData({ ...customerData, paymentMode: e.target.value })}
                      className="admin-select"
                      required={bookingType === 'customer'}
                    >
                      <option value="cash">Cash</option>
                      <option value="upi">UPI</option>
                      <option value="card">Card</option>
                      <option value="online">Online</option>
                    </select>
                  </div>
                </div>
                <div className="admin-form-row">
                  <div className="admin-field">
                    <label className="admin-label" style={{ fontSize: '12px' }}>Email (Optional)</label>
                    <input
                      type="email"
                      value={customerData.email}
                      onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                      placeholder="Enter email"
                      className="admin-input"
                    />
                  </div>
                  <div className="admin-field">
                    <label className="admin-label" style={{ fontSize: '12px' }}>Notes (Optional)</label>
                    <input
                      type="text"
                      value={bookingNotes}
                      onChange={(e) => setBookingNotes(e.target.value)}
                      placeholder="Any additional notes"
                      className="admin-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Block Reason (shown for block slots) */}
            {bookingType === 'block' && (
              <div className="admin-field" style={{ marginBottom: '16px' }}>
                <label className="admin-label">Reason for Blocking *</label>
                <textarea
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  required={bookingType === 'block'}
                  minLength={10}
                  rows={2}
                  placeholder="E.g., Training sessions, Maintenance..."
                  className="admin-input"
                  style={{ resize: 'vertical' }}
                />
                <span style={{ fontSize: '11px', color: '#6b7280' }}>{bookingNotes.length}/10 min</span>
              </div>
            )}

            {/* Options */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={ignoreUnavailable} onChange={(e) => setIgnoreUnavailable(e.target.checked)} />
                <span>Skip already booked slots</span>
              </label>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '8px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button type="button" onClick={handleReset} disabled={loading} className="admin-btn admin-btn-secondary">Reset</button>
              <button type="submit" disabled={loading} className="admin-btn admin-btn-primary">
                {loading ? (bookingType === 'customer' ? 'Creating Bookings...' : 'Blocking...') : (bookingType === 'customer' ? 'Create Bulk Booking' : 'Block Slots')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminBulkBooking;
